import React, { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Container, Row, Col } from "./Tailwind";
import { Link, useLocation } from "react-router-dom";
import { useUserStore } from "../../store/store";
import { BsHouse, BsHouseGear, BsPerson, BsPersonBadge, BsPersonGear } from "react-icons/bs";
import { X } from "lucide-react";
import { changeCancelableUserConfirm, changeCancelableUserRead } from "../../api/services/auth.service";
import { packageService, getUserOrdersByDate } from "../../api/services/package.service";
import { useAuthContext } from "../AuthContext";
import moment from "moment";
import { userService } from "../../api/services/user.service";
import { getUserPreCoupons } from "../../api/services/coupon.service";
import {
  dismissDailyPopupForToday,
  isDailyPopupDismissed,
} from "./dailyPopupDismiss.mjs";
import { SUBDOMAIN } from "../../assets/meta/meta";

const MODAL_STACK = {
  RAIN_CANCEL: "RAIN_CANCEL",
  VOUCHER: "VOUCHER",
  DEPOSIT: "DEPOSIT",
  KAKAO_CHANNEL_AGREE: "KAKAO_CHANNEL_AGREE",
  PRECOUPON: "PRECOUPON",
};

const DAILY_POPUP_TYPES = {
  PRECOUPON: "precoupon",
  VOUCHER: "voucher",
};

const Navbar = (props: { hideHeader?: boolean }) => {
  const location = useLocation();
  const { hideHeader = false } = props;

  const { userInfo, isUserLogin, logout } = useAuthContext();

  const [user, setUser, site, siteDetail, setSite] = useUserStore((state) => [
    state.user,
    state.setUser,
    state.site,
    state.siteDetail,
    state.setSite,
  ]);
  const siteName = site?.name || siteDetail?.name || "AmazingTicket";

  const [rainCancelData, setRainCancelData] = useState<any>(null);

  useEffect(() => {
    console.log("Navbar useEffect", location.pathname);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const checkKakaoLoginStatus = useCallback(async (user: any, silent = false) => {
    try {
      const response = await userService.getKakaoChannels();
      const data = response.data;
      if (data.result) {
        setUser({ ...user, kakaoChannelLinked: data.result });
        return true;
      } else {
        if (!silent) stackModal(MODAL_STACK.KAKAO_CHANNEL_AGREE);
      }
    } catch (error: any) {
      console.log(error.response?.data);
    }
    return false;
  }, [setUser]);

  useEffect(() => {
    if (!user) return;

    const fetchUserData = async () => {
      if (user?.id) {
        let pathname = location.pathname;
        if (pathname === "/") pathname = "";

        try {
          await userService.getUserById(user.id, pathname);
        } catch (error) {
          // console.log("access record error", error);
        }
      }
    };
    fetchUserData();
  }, [user, location]);

  useEffect(() => {
    if (!user) return;

    setTodaysReservations([]);

    const fetchCancelData = async () => {
      try {
        const response = await userService.getCancelableUsers();
        const data = response.data;
        if (data?.result && data.result?.is_confirmed === false) {
          setRainCancelData(data.result);
          stackModal(MODAL_STACK.RAIN_CANCEL);
          await changeCancelableUserRead();
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchCancelData();

    const today = moment().format("YYYY-MM-DD");

    const fetchOrderData = async () => {
      try {
        const response = await getUserOrdersByDate(today, SUBDOMAIN);
        const data = await response.data as any;
        // console.log("user-order", data);

        if (data.results.length > 0) {
          const _reservs = data.results.map((order: any) => ({
            ...order,
            reserv_id: order.id,
          }));
          setTodaysReservations((prev) => [...prev, ..._reservs]);
        }
      } catch (e) {
        console.log(e);
      }
      return
    };
    fetchOrderData();

    const fetchReservationGiftData = async () => {
      try {
        const response = await packageService.getReservationGifts(user.phone, today);
        const data = await response.data;
        // console.log("user-order", data);

        if (data.length > 0) {
          setTodaysReservations((prev: any[]) => [...prev, ...data]);
        }
      } catch (e) {
        console.log(e);
      }
      return
    };
    fetchReservationGiftData();

    const fetchPrecouponData = async ({ pageParam = 1 }: { pageParam?: number }) => {
      try {
        const response = await getUserPreCoupons(pageParam);
        setPrecouponData(response.data.results);
        if (
          response.data.results &&
          response.data.results.length > 0 &&
          !isDailyPopupDismissed(window.localStorage, DAILY_POPUP_TYPES.PRECOUPON, user?.id)
        ) {
          stackModal(MODAL_STACK.PRECOUPON);
        }
      } catch (error) {
        console.log(error);
      }
    };

    fetchPrecouponData({ pageParam: 1 });

    // if (!user?.kakaoChannelLinked) {
    //   checkKakaoLoginStatus(user, true);
    // }
  }, [user]);

  // ======================================================================================================
  //    Modals settings
  // ======================================================================================================

  const [modalStack, setModalStack] = useState<string[]>([]);
  const stackModal = (modal: string) => {
    setModalStack((prev) => {
      const _prev = [...prev];
      if (_prev.indexOf(modal) === -1) {
        _prev.push(modal);
      }
      return _prev;
    });
  };
  const closeModal = (modal: string) => {
    const _modalStack = [...modalStack];
    const idx = _modalStack.indexOf(modal);
    if (idx !== -1) {
      _modalStack.splice(idx, 1);
    }
    setModalStack(_modalStack);
  };
  const isModalOpened = (modal: string) => {
    if (!["", "/"].includes(location.pathname)) {
      return false;
    }
    return modalStack.length > 0 && modalStack[0] === modal;
  };

  const [cancelChecked, setCancelChecked] = useState(false);

  const [todaysReservations, setTodaysReservations] = useState<any[]>([]);
  const [depositReservId, setDepositReservId] = useState(null);

  const [precouponData, setPrecouponData] = useState<any[] | null>(null);

  const hideDailyPopupForToday = (modal: string, popupType: string) => {
    dismissDailyPopupForToday(window.localStorage, popupType, user?.id ?? userInfo?.id);
    closeModal(modal);
  };

  const changeCancelableUserConfirmHandler = async (isConfirm: boolean) => {
    try {
      await changeCancelableUserConfirm(isConfirm);
      console.log("confirm success");
      closeModal(MODAL_STACK.RAIN_CANCEL);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    closeModal(MODAL_STACK.DEPOSIT);

    for (let i = 0; i < todaysReservations.length; i++) {
      if (todaysReservations[i].gifted) {
        continue;
      }
      if (todaysReservations[i].reserv_state && todaysReservations[i].reserv_state !== "예약") {
        continue;
      }
      if (todaysReservations[i].state && todaysReservations[i].state !== "예약") {
        continue;
      }

      if (todaysReservations[i].depositRequired) {
        setDepositReservId(todaysReservations[i].reserv_id);
        stackModal(MODAL_STACK.DEPOSIT);
        break;
      }
    }
  }, [todaysReservations]);

  return (
    <React.Fragment>
      {!hideHeader && (
        <div className="sticky top-0 bg-white border-b border-gray-200 z-999 py-1 px-2">
          <nav className="flex items-center" id="navbar">
            <div className="container mx-4 flex justify-between items-center h-14">
              <div className="flex justify-center">
                <Link className="text-xl font-bold flex items-center" to="/" style={{ fontFamily: "Jalnan", color: "var(--color-primary)" }}>
                  {React.createElement(BsHouse as any, { size: "25" })}
                </Link>
              </div>

              <div className="flex flex-row justify-center items-center gap-3">
                {!user ? (
                  <Link to={`/login`} className="ml-3" style={{ cursor: "pointer" }}>
                    {React.createElement(BsPerson as any, { size: "25" })}
                  </Link>
                ) : (
                  <Link to="/my-page" className="flex items-center" style={{ color: "#404040", lineHeight: "40px", fontFamily: "Gmarket-M" }}>
                    {React.createElement(BsPerson as any, { size: "25" })}
                  </Link>
                )}
              </div>
            </div>
          </nav>
        </div>
      )}

      {/* 
            // ======================================================================================================
            //    Modals
            // ====================================================================================================== 
            */}

      {/* 우천 취소 모달 확인 팝업 창. */}
      <Modal isOpen={!!isModalOpened(MODAL_STACK.RAIN_CANCEL)} toggle={() => closeModal(MODAL_STACK.RAIN_CANCEL)} centered>
        <ModalHeader toggle={() => closeModal(MODAL_STACK.RAIN_CANCEL)} className="border-0">
          <div className="flex justify-center"></div>
        </ModalHeader>
        <ModalBody className="text-center">
          <div className="flex justify-center">
            <div className="flex flex-col justify-center items-center" style={{ paddingInline: 18 }}>
              <div className="flex justify-center">
                <i className="ri-alert-line ri-xl text-danger"></i>
              </div>
              <div className="flex justify-center mt-2">
                <p style={{ fontWeight: "bold" }}>{rainCancelData && rainCancelData.cancel_day} 취소 안내</p>
              </div>

              {rainCancelData && rainCancelData.items.length && rainCancelData.items[0].description ? (
                <div className="mt-3" style={{ textAlign: "left" }} dangerouslySetInnerHTML={rainCancelData.items[0].description}>
                </div>
              ) : (
                <div className="mt-3" style={{ textAlign: "left" }}>
                  <p>안녕하세요, 고객님! 어메이징캠프는 우천 시에도 정상운영합니다.</p>
                  <p>우천 시 취소 및 환불도 기존 환불 규정과 동일하게 적용됩니다.</p>
                  <p>마이페이지 구매내역에서 예약 상태와 환불 가능 여부를 확인해 주세요.</p>
                  <br />
                  <p>*어메이징캠프 환불정책</p>
                  <p>-이용일 3일전: 100% 자동환불<br />
                    -이용 이틀전 : 70% 환불(30%차감)<br />
                    -이용 하루전: 50% 환불<br />
                    -이용 당일 : 환불불가</p><br />

                  <p>본 메시지는 우천 시 발송되는 안내입니다.</p>
                </div>
              )}
              <div className="mt-3">
                <input type="checkbox" id="cancelableCheck" onChange={(e) => setCancelChecked(true)} />
                <label htmlFor="cancelableCheck" className="ms-2">
                  위 내용을 확인하였습니다.
                </label>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter className="border-0">
          <div className="flex flex-row justify-content-end w-full gap-3">
            <Button
              color="dark"
              onClick={() => {
                closeModal(MODAL_STACK.RAIN_CANCEL);
              }}
              className="rounded-pill"
            >
              닫기
            </Button>
            <Button
              color="secondary"
              onClick={() => {
                if (cancelChecked) changeCancelableUserConfirmHandler(true);
                else toast.warning("위 내용을 확인해주세요.");
              }}
              className="rounded-pill"
            >
              확인
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      {/* <div>{JSON.stringify(modalStack)}</div> */}

      {/* Deposit Modal */}
      <Modal isOpen={!!isModalOpened(MODAL_STACK.DEPOSIT)} toggle={() => closeModal(MODAL_STACK.DEPOSIT)} centered overlayClassName="backdrop-blur-[2px] bg-black/30">
        <div className="flex justify-end items-center px-1 pt-1 pb-2 border-b border-gray-200">
          <button
            type="button"
            className="inline-flex items-center justify-center w-10 h-10 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors active:scale-[0.96] focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
            onClick={() => {
              closeModal(MODAL_STACK.DEPOSIT);
            }}
            aria-label="Close"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>
        <ModalBody className="text-center p-5">
          <div className="">
            <h5 className="modal-title mb-3 text-balance break-keep animate-in fade-in-0 slide-in-from-bottom-2 duration-300 [animation-fill-mode:both]">
              체크인 하기 전에
              <br />
              청소보증금을 결제해 주세요.
            </h5>
            <p className="text-muted mb-4 text-pretty break-keep animate-in fade-in-0 slide-in-from-bottom-2 duration-300 [animation-delay:75ms] [animation-fill-mode:both]">
              결제화면으로 이동하시겠습니까?
            </p>
            <div className="flex flex-col gap-2 mb-4 animate-in fade-in-0 slide-in-from-bottom-2 duration-300 [animation-delay:150ms] [animation-fill-mode:both]">
              {todaysReservations
                .filter((reservation) => !!reservation.depositRequired && reservation.reserv_id === depositReservId)
                .map((reservation, idx) => {
                  return (
                    <div key={idx} className="flex items-center justify-center gap-2.5 rounded-xl bg-gray-50 px-4 py-3">
                      <span className="font-medium text-gray-900 text-pretty">
                        {reservation.name} {reservation.reservationDate} {reservation.spot}번
                      </span>
                    </div>
                  );
                })}
              <div className="rounded-xl bg-gray-50 px-4 py-3 text-left text-sm text-gray-600 break-keep">
                <p className="font-semibold text-gray-900 mb-1">청소보증금이란?</p>
                <p>
                  청결한 시설 관리 및 비품 관리를 위해 패키지 상품 구매 시 청소보증금이 결제됩니다. 시설 이용 후 정리를 완료하고
                  체크아웃 버튼을 누르면 관리자가 정리 사항을 확인합니다.
                </p>
                <p className="text-red-500 mt-1">체크아웃 요청이 승인 되면 자동 환불됩니다.</p>
              </div>
            </div>
            <div className="flex flex-col gap-2 animate-in fade-in-0 slide-in-from-bottom-2 duration-300 [animation-delay:225ms] [animation-fill-mode:both]">
              <Button
                asChild
                variant="primary"
                className="flex-1 min-h-[44px] text-sm font-semibold shadow-sm whitespace-nowrap transition active:scale-[0.96]"
              >
                <Link to={`/add-options-order?id=${depositReservId}&deposit=true`} state={{ tab: "3" }}>
                  결제하기
                </Link>
              </Button>
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* <Modal isOpen={!!isModalOpened(MODAL_STACK.KAKAO_CHANNEL_AGREE)} toggle={() => closeModal(MODAL_STACK.KAKAO_CHANNEL_AGREE)} backdrop={"static"} id="staticBackdrop-deposit" centered>
        <ModalHeader>
          <Button
            type="button"
            className="btn-close"
            onClick={() => {
              closeModal(MODAL_STACK.KAKAO_CHANNEL_AGREE);
            }}
            aria-label="Close"
          ></Button>
        </ModalHeader>
        <ModalBody className="text-center px-5">
          <h4 className="text-center" style={{ fontFamily: "Gmarket-M", color: "#f6475f" }}>
            서비스 추가동의 안내
          </h4>
          <div className="pt-3 pb-2" style={{ textAlign: "left" }}>
            <h5 className="mb-2" style={{ fontFamily: "Gmarket-M" }}>
              안녕하세요, {user?.username}님
            </h5>
            <p className="my-2 fs-14">
              어메이징티켓에서 카카오 로그인을 이용한 고객님에게
              <br />더 나은 서비스를 제공하기 위해 고객님의 동의가 필요합니다.
            </p>
            <p className="my-3 fs-14">고객님의 정보는 아래의 혜택 제공을 위해 사용됩니다.</p>
            <p className="my-2 fs-14">* 맞춤 캠핑장 추천</p>
            <p className="my-2 fs-14">* 특별 할인 및 프로모션 정보</p>
            <p className="my-2 fs-14 mb-3">* 신상품 업데이트</p>
            <p className="my-2 fs-14">
              고객님의 정보는 안전하게 보호됩니다.
              <br />
              언제든 동의를 철회할 수 있습니다.
            </p>

            <p className="my-2 fs-14">동의하기를 누르시면 카카오 로그인으로 이동합니다.</p>
          </div>
          <div className="pt-3 pb-2"></div>
          <Row className="justify-center items-center">
            <Col>
              <Button
                style={{ fontFamily: "Gmarket-M", backgroundColor: "#f6475f", borderColor: "#f6475f" }}
                variant="success"
                className="w-full "
                onClick={() => {
                  closeModal(MODAL_STACK.KAKAO_CHANNEL_AGREE);
                  onLogout("/login");
                }}
              >
                동의하기
              </Button>
            </Col>
          </Row>
          <div className="pt-2"></div>
        </ModalBody>
      </Modal> */}

      {/* Precoupon Modal */}
      <Modal isOpen={!!isModalOpened(MODAL_STACK.PRECOUPON)} toggle={() => closeModal(MODAL_STACK.PRECOUPON)} centered overlayClassName="backdrop-blur-[2px] bg-black/30">
        <div className="flex justify-end items-center px-1 pt-1 pb-2 border-b border-gray-200">
          <button
            type="button"
            className="inline-flex items-center justify-center w-10 h-10 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors active:scale-[0.96] focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300"
            onClick={() => {
              closeModal(MODAL_STACK.PRECOUPON);
            }}
            aria-label="Close"
          >
            <X size={18} strokeWidth={2} />
          </button>
        </div>
        <ModalBody className="text-center p-5">
          <div className="">
            <h5 className="modal-title mb-3 text-balance break-keep animate-in fade-in-0 slide-in-from-bottom-2 duration-300 [animation-fill-mode:both]" id="staticBackdropLabel">
              사용 가능한 어메이징패스 예약권이 있습니다.
            </h5>
            <p className="text-muted mb-4 text-pretty break-keep animate-in fade-in-0 slide-in-from-bottom-2 duration-300 [animation-delay:75ms] [animation-fill-mode:both]">
              예약권은 마이페이지-어메이징패스 메뉴에서 사용가능합니다.
            </p>
            <div className="flex flex-col gap-2 mb-5 animate-in fade-in-0 slide-in-from-bottom-2 duration-300 [animation-delay:150ms] [animation-fill-mode:both]">
              {precouponData && precouponData.length > 0 ? precouponData.slice(0, 1).map((item, idx) => {
                return (
                  <div key={item.id} className="flex items-center justify-center gap-2.5 rounded-xl bg-gray-50 px-4 py-3">
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.precoupon_name}
                        className="rounded-lg outline outline-1 -outline-offset-1 outline-black/10 object-cover shrink-0"
                        style={{ width: "32px", height: "32px" }}
                      />
                    )}
                    <span className="font-medium text-gray-900 text-pretty">{item.precoupon_name}</span>
                  </div>
                );
              }) : null}
            </div>
            <div className="flex flex-col gap-2 animate-in fade-in-0 slide-in-from-bottom-2 duration-300 [animation-delay:225ms] [animation-fill-mode:both]">
              <div className="flex flex-row items-stretch gap-2">
                <Button
                  asChild
                  variant="primary"
                  className="flex-1 min-h-[44px] text-sm font-semibold shadow-sm whitespace-nowrap transition active:scale-[0.96]"
                >
                  <Link to="/amazingpass">사용하러 가기</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="flex-1 min-h-[44px] text-sm font-semibold shadow-sm whitespace-nowrap transition active:scale-[0.96]"
                >
                  <Link to="/my-page/amazingpass">확인하러 가기</Link>
                </Button>
              </div>
              <label className="inline-flex justify-center items-center gap-2 min-h-[40px] text-sm font-medium text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  onChange={(e) => {
                    if (e.target.checked) {
                      hideDailyPopupForToday(MODAL_STACK.PRECOUPON, DAILY_POPUP_TYPES.PRECOUPON);
                    }
                  }}
                  className="w-4 h-4 cursor-pointer accent-[var(--color-primary)]"
                />
                오늘 하루 보지 않기
              </label>
            </div>
          </div>
        </ModalBody>
      </Modal>


    </React.Fragment>
  );
};

export default Navbar;
// export default TempNavbar;
